import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Snackbar, Alert, CircularProgress, MenuItem, Select,
  FormControl, InputLabel, SelectChangeEvent
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { 
  getAllLLMPromptConfigs, 
  createLLMPromptConfig, 
  updateLLMPromptConfig, 
  deleteLLMPromptConfig,
  getReviewDefinitions,
  getReviewEntityTypes
} from '../../services/api';

interface ReviewDefinition {
  id: number;
  name: string;
  display_label: string;
  description: string;
}

interface ReviewEntityType {
  id: number;
  name: string;
  display_label: string;
  category: string;
  description: string;
}

interface LLMPromptConfig {
  id: number;
  review_definition_id: number;
  review_entity_type_id: number;
  prompt_text: string;
  review_definition?: ReviewDefinition;
  review_entity_type?: ReviewEntityType;
  created_at?: string;
  updated_at?: string;
}

const LLMPromptConfigSetup: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<LLMPromptConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const [openViewDialog, setOpenViewDialog] = useState<boolean>(false);
  const [viewConfig, setViewConfig] = useState<LLMPromptConfig | null>(null);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'add'>('edit');
  const [currentConfig, setCurrentConfig] = useState<Partial<LLMPromptConfig>>({
    review_definition_id: 0,
    review_entity_type_id: 0,
    prompt_text: ''
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [availableDefinitions, setAvailableDefinitions] = useState<ReviewDefinition[]>([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState<ReviewEntityType[]>([]);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      // Fetch all LLM prompt configs
      const configsResponse = await getAllLLMPromptConfigs();
      setConfigs(configsResponse.data);
      
      // Fetch all available review definitions
      const definitionsResponse = await getReviewDefinitions();
      setAvailableDefinitions(definitionsResponse.data);
      
      // Fetch all available review entity types
      const entityTypesResponse = await getReviewEntityTypes();
      setAvailableEntityTypes(entityTypesResponse.data);
    } catch (err) {
      setError('Failed to fetch LLM prompt configurations');
      console.error('Error fetching LLM prompt configs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setDeleteTarget(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget === null) return;
    
    try {
      await deleteLLMPromptConfig(deleteTarget);
      setSnackbar({
        open: true,
        message: 'LLM prompt configuration deleted successfully',
        severity: 'success'
      });
      fetchConfigs();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete LLM prompt configuration',
        severity: 'error'
      });
      console.error('Error deleting config:', err);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenViewDialog = (config: LLMPromptConfig) => {
    setViewConfig(config);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewConfig(null);
  };

  const handleOpenEditDialog = (config?: LLMPromptConfig) => {
    if (config) {
      setEditMode('edit');
      setCurrentConfig({
        id: config.id,
        review_definition_id: config.review_definition_id,
        review_entity_type_id: config.review_entity_type_id,
        prompt_text: config.prompt_text
      });
    } else {
      setEditMode('add');
      setCurrentConfig({
        review_definition_id: availableDefinitions[0]?.id || 0,
        review_entity_type_id: availableEntityTypes[0]?.id || 0,
        prompt_text: ''
      });
    }
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setCurrentConfig({
      review_definition_id: 0,
      review_entity_type_id: 0,
      prompt_text: ''
    });
  };

  const handleSaveConfig = async () => {
    try {
      if (editMode === 'add') {
        await createLLMPromptConfig({
          review_definition_id: currentConfig.review_definition_id!,
          review_entity_type_id: currentConfig.review_entity_type_id!,
          prompt_text: currentConfig.prompt_text!
        });
        setSnackbar({
          open: true,
          message: 'LLM prompt configuration created successfully',
          severity: 'success'
        });
      } else {
        await updateLLMPromptConfig({
          llm_prompt_config_id: currentConfig.id!,
          review_definition_id: currentConfig.review_definition_id!,
          review_entity_type_id: currentConfig.review_entity_type_id!,
          prompt_text: currentConfig.prompt_text!
        });
        setSnackbar({
          open: true,
          message: 'LLM prompt configuration updated successfully',
          severity: 'success'
        });
      }
      fetchConfigs();
      handleCloseEditDialog();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save LLM prompt configuration',
        severity: 'error'
      });
      console.error('Error saving config:', err);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setCurrentConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box>
        <TitleBar />
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <TitleBar />
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <TitleBar />
      <Container sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/organization-setup')} 
            aria-label="Back"
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1,
              backgroundColor: '#f5f5f5',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
              mr: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" gutterBottom>
            LLM Prompt Configuration
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Configure LLM prompts for different review definitions and entity types
        </Typography>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditDialog()}
          >
            Add New Configuration
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Review Definition</strong></TableCell>
                <TableCell><strong>Entity Type</strong></TableCell>
                <TableCell><strong>Prompt Preview</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No LLM prompt configurations found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>{config.id}</TableCell>
                    <TableCell>
                      {config.review_definition?.display_label || config.review_definition_id}
                    </TableCell>
                    <TableCell>
                      {config.review_entity_type?.display_label || config.review_entity_type_id}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {config.prompt_text}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="info"
                        onClick={() => handleOpenViewDialog(config)}
                        title="View"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(config)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(config.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this LLM prompt configuration? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog 
          open={openViewDialog} 
          onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>View LLM Prompt Configuration</DialogTitle>
          <DialogContent>
            {viewConfig && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Review Definition:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewConfig.review_definition?.display_label}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Entity Type:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewConfig.review_entity_type?.display_label}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Prompt Text:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewConfig.prompt_text}
                  </Typography>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Dialog */}
        <Dialog 
          open={openEditDialog} 
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editMode === 'add' ? 'Add New LLM Prompt Configuration' : 'Edit LLM Prompt Configuration'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Review Definition</InputLabel>
                <Select
                  value={currentConfig.review_definition_id || ''}
                  label="Review Definition"
                  onChange={(e: SelectChangeEvent<number>) => 
                    handleInputChange('review_definition_id', e.target.value)
                  }
                >
                  {availableDefinitions.map((def) => (
                    <MenuItem key={def.id} value={def.id}>
                      {def.display_label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={currentConfig.review_entity_type_id || ''}
                  label="Entity Type"
                  onChange={(e: SelectChangeEvent<number>) => 
                    handleInputChange('review_entity_type_id', e.target.value)
                  }
                >
                  {availableEntityTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.display_label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={12}
                label="Prompt Text"
                value={currentConfig.prompt_text || ''}
                onChange={(e) => handleInputChange('prompt_text', e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveConfig} 
              color="primary" 
              variant="contained"
              disabled={!currentConfig.prompt_text || !currentConfig.review_definition_id || !currentConfig.review_entity_type_id}
            >
              {editMode === 'add' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default LLMPromptConfigSetup;
